import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PackageCheck, Plus, ShoppingCart, Trash2 } from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  type BadgeTone,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { listarBodegas, listarInsumos, listarProveedores } from "../inventario/api";
import {
  anularOrdenCompra,
  anularRecepcion,
  crearOrdenCompra,
  listarOrdenesCompra,
  listarSolicitudes,
  obtenerOrdenCompra,
  recibirOrdenCompra,
  type DetalleOCIn,
  type EstadoOC,
  type LineaRecepcion,
  type LineaRecepcionMedidor,
  type OrdenCompraRead,
  type RecibirOCBody,
} from "./api";

const ESTADOS: EstadoOC[] = ["Emitida", "RecibidaParcial", "Recibida", "Anulada"];

const ESTADO_TONE: Record<EstadoOC, BadgeTone> = {
  Emitida: "brand",
  RecibidaParcial: "amber",
  Recibida: "emerald",
  Anulada: "slate",
};

function nroOC(o: OrdenCompraRead): string {
  return `${o.anio}-${String(o.numero).padStart(4, "0")}`;
}

export function OrdenesCompraPage(): JSX.Element {
  const [creando, setCreando] = useState(false);
  const [detalle, setDetalle] = useState<number | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<EstadoOC | "">("");

  const ordenesQ = useQuery({
    queryKey: ["compras", "ordenes-compra", filtroEstado || null],
    queryFn: () => listarOrdenesCompra(filtroEstado || undefined),
  });
  const proveedoresQ = useQuery({
    queryKey: ["inventario", "proveedores"],
    queryFn: listarProveedores,
  });

  const proveedorNombre = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of proveedoresQ.data ?? [])
      m.set(p.id, p.nombre_fantasia ?? p.razon_social);
    return m;
  }, [proveedoresQ.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Órdenes de compra"
        icon={ShoppingCart}
        subtitle={ordenesQ.data && <span>({ordenesQ.data.length})</span>}
        actions={
          <Button icon={Plus} onClick={() => setCreando(true)}>
                    Nueva OC
                  </Button>
        }
      />

      <div className="flex items-center gap-2">
        <label className="text-sm text-neutral-600">Estado</label>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value as EstadoOC | "")}
          className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
        >
          <option value="">Todas</option>
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </div>

      {ordenesQ.isPending && <Cargando />}
      {ordenesQ.isError && (
        <ErrorCarga error={ordenesQ.error} onReintentar={() => ordenesQ.refetch()} />
      )}

      {ordenesQ.data && ordenesQ.data.length === 0 && (
        <EmptyState
          icon={ShoppingCart}
          title="Sin órdenes de compra"
          description="Emite una orden de compra a un proveedor, desde una solicitud aprobada o directa."
        />
      )}

      {ordenesQ.data && ordenesQ.data.length > 0 && (
        <TableWrap label="Órdenes de compra">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">N°</th>
                <th scope="col" className="px-3 py-2 text-left">Proveedor</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Monto</th>
                <th scope="col" className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {ordenesQ.data.map((o) => (
                <tr key={o.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 font-mono text-xs text-neutral-700">{nroOC(o)}</td>
                  <td className="px-3 py-2 text-neutral-700">
                    {proveedorNombre.get(o.proveedor_id) ?? `Proveedor #${o.proveedor_id}`}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={ESTADO_TONE[o.estado]}>{o.estado}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCLP(o.monto_total)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="link" size="sm" onClick={() => setDetalle(o.id)}>
                      Ver / recibir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {creando && <CrearOCModal onClose={() => setCreando(false)} />}
      {detalle != null && <OCDetailModal id={detalle} onClose={() => setDetalle(null)} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Crear OC
// ────────────────────────────────────────────────────────────────────────

interface LineaForm {
  descripcion: string;
  cantidad: string;
  costo_unitario: string;
  insumo_id: string;
  es_medidor: boolean;
}

const LINEA_VACIA: LineaForm = {
  descripcion: "",
  cantidad: "",
  costo_unitario: "",
  insumo_id: "",
  es_medidor: false,
};

function CrearOCModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [proveedorId, setProveedorId] = useState("");
  const [solicitudId, setSolicitudId] = useState("");
  const [bodegaId, setBodegaId] = useState("");
  const [condiciones, setCondiciones] = useState("");
  const [lineas, setLineas] = useState<LineaForm[]>([{ ...LINEA_VACIA }]);
  const [error, setError] = useState<string | null>(null);

  const proveedoresQ = useQuery({
    queryKey: ["inventario", "proveedores"],
    queryFn: listarProveedores,
  });
  const solicitudesQ = useQuery({
    queryKey: ["compras", "solicitudes", "Aprobada"],
    queryFn: () => listarSolicitudes("Aprobada"),
  });
  const bodegasQ = useQuery({ queryKey: ["inventario", "bodegas"], queryFn: listarBodegas });
  const insumosQ = useQuery({ queryKey: ["inventario", "insumos"], queryFn: listarInsumos });

  const mut = useMutation({
    mutationFn: () => {
      const detalles: DetalleOCIn[] = lineas
        .filter((l) => l.descripcion.trim() && Number(l.cantidad) > 0)
        .map((l) => {
          const d: DetalleOCIn = {
            descripcion: l.descripcion.trim(),
            cantidad: Number(l.cantidad),
            costo_unitario: Number(l.costo_unitario) || 0,
          };
          if (l.es_medidor) d.es_medidor = true;
          else if (l.insumo_id) d.insumo_id = Number(l.insumo_id);
          return d;
        });
      return crearOrdenCompra({
        proveedor_id: Number(proveedorId),
        solicitud_id: solicitudId ? Number(solicitudId) : null,
        bodega_destino_id: bodegaId ? Number(bodegaId) : null,
        condiciones_pago: condiciones.trim() || null,
        detalles,
      });
    },
    onSuccess: (o) => {
      toast.success(`OC ${nroOC(o)} emitida`);
      void qc.invalidateQueries({ queryKey: ["compras", "ordenes-compra"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo emitir";
      setError(msg);
      toast.error(msg);
    },
  });

  function setLinea(i: number, patch: Partial<LineaForm>): void {
    setLineas((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  function submit(): void {
    setError(null);
    if (!proveedorId) return setError("Elige un proveedor");
    const validas = lineas.filter((l) => l.descripcion.trim() && Number(l.cantidad) > 0);
    if (validas.length === 0) return setError("Agrega al menos una línea con cantidad mayor a 0");
    mut.mutate();
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([proveedorId, solicitudId, bodegaId, condiciones, lineas]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva orden de compra"
      icon={ShoppingCart}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? "Emitiendo…" : "Emitir OC"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Select
          label="Proveedor"
          value={proveedorId}
          onChange={(e) => setProveedorId(e.target.value)}
        >
          <option value="">— Elegir —</option>
          {(proveedoresQ.data ?? [])
            .filter((p) => p.activo)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre_fantasia ?? p.razon_social}
              </option>
            ))}
        </Select>
        <Select
          label="Solicitud (SIC) opcional"
          value={solicitudId}
          onChange={(e) => setSolicitudId(e.target.value)}
        >
          <option value="">— Sin SIC (directa) —</option>
          {(solicitudesQ.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.anio}-{String(s.numero).padStart(4, "0")} · {s.justificacion.slice(0, 40)}
            </option>
          ))}
        </Select>
        <Select
          label="Bodega destino (opcional)"
          value={bodegaId}
          onChange={(e) => setBodegaId(e.target.value)}
        >
          <option value="">— Sin bodega —</option>
          {(bodegasQ.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Condiciones de pago (opcional)"
          value={condiciones}
          onChange={(e) => setCondiciones(e.target.value)}
        />
      </div>

      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Líneas de la orden
          </div>
          <Button
            variant="link"
            size="sm"
            icon={Plus}
            onClick={() => setLineas((prev) => [...prev, { ...LINEA_VACIA }])}
          >
            Agregar línea
          </Button>
        </div>
        <div className="space-y-2">
          {lineas.map((l, i) => (
            <div
              key={i}
              className="grid grid-cols-1 gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2 md:grid-cols-12"
            >
              <div className="md:col-span-4">
                <Input
                  label="Descripción"
                  value={l.descripcion}
                  onChange={(e) => setLinea(i, { descripcion: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Input
                  label="Cantidad"
                  type="number"
                  min={0}
                  value={l.cantidad}
                  onChange={(e) => setLinea(i, { cantidad: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Input
                  label="Costo unit. $"
                  type="number"
                  min={0}
                  value={l.costo_unitario}
                  onChange={(e) => setLinea(i, { costo_unitario: e.target.value })}
                />
              </div>
              <div className="md:col-span-3">
                {l.es_medidor ? (
                  <div>
                    <div className="text-xs font-medium text-neutral-500">Ítem</div>
                    <div className="flex h-9 items-center text-sm text-brand-700">
                      → Padrón de medidores
                    </div>
                  </div>
                ) : (
                  <Select
                    label="Ítem (opcional)"
                    value={l.insumo_id}
                    onChange={(e) => setLinea(i, { insumo_id: e.target.value })}
                  >
                    <option value="">— Ítem no catalogado —</option>
                    {(insumosQ.data ?? [])
                      .filter((ins) => ins.activo)
                      .map((ins) => (
                        <option key={ins.id} value={ins.id}>
                          {ins.nombre} ({ins.unidad_medida})
                        </option>
                      ))}
                  </Select>
                )}
                <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-neutral-600">
                  <input
                    type="checkbox"
                    checked={l.es_medidor}
                    onChange={(e) =>
                      setLinea(i, { es_medidor: e.target.checked, insumo_id: "" })
                    }
                    className="h-3.5 w-3.5 rounded border-neutral-450"
                  />
                  Es medidor
                </label>
              </div>
              <div className="flex items-end md:col-span-1">
                <Button
                  variant="linkDanger"
                  size="sm"
                  icon={Trash2}
                  disabled={lineas.length === 1}
                  onClick={() => setLineas((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <span className="sr-only">Quitar</span>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Detalle + recepción
// ────────────────────────────────────────────────────────────────────────

function OCDetailModal({ id, onClose }: { id: number; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [bodegaId, setBodegaId] = useState("");
  const [docTipo, setDocTipo] = useState("");
  const [docNumero, setDocNumero] = useState("");
  const [anulandoRecepcion, setAnulandoRecepcion] = useState<number | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [recepcion, setRecepcion] = useState<Record<number, string>>({});
  const [recepcionMed, setRecepcionMed] = useState<
    Record<number, { series: string; marca: string; diametro: string }>
  >({});

  const ocQ = useQuery({
    queryKey: ["compras", "orden-compra", id],
    queryFn: () => obtenerOrdenCompra(id),
  });
  const bodegasQ = useQuery({ queryKey: ["inventario", "bodegas"], queryFn: listarBodegas });

  function invalidar(): void {
    void qc.invalidateQueries({ queryKey: ["compras", "orden-compra", id] });
    void qc.invalidateQueries({ queryKey: ["compras", "ordenes-compra"] });
    void qc.invalidateQueries({ queryKey: ["inventario"] });
  }

  const recibirMut = useMutation({
    mutationFn: (body: RecibirOCBody) => recibirOrdenCompra(id, body),
    onSuccess: () => {
      toast.success("Recepción registrada");
      setRecepcion({});
      setRecepcionMed({});
      invalidar();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo registrar la recepción"),
  });

  const anularMut = useMutation({
    mutationFn: () => anularOrdenCompra(id),
    onSuccess: () => {
      toast.success("OC anulada");
      invalidar();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo anular"),
  });

  const anularRecepcionMut = useMutation({
    mutationFn: (args: { movimientoId: number; motivo: string }) =>
      anularRecepcion(id, args.movimientoId, args.motivo),
    onSuccess: () => {
      toast.success("Recepción anulada: el stock volvió a descontarse");
      setAnulandoRecepcion(null);
      setMotivoAnulacion("");
      invalidar();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo anular la recepción"),
  });

  const oc = ocQ.data;
  const puedeRecibir = oc?.estado === "Emitida" || oc?.estado === "RecibidaParcial";

  function parseSeries(texto: string): string[] {
    return texto
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function registrarRecepcion(): void {
    if (!oc) return;
    const lineas: LineaRecepcion[] = oc.detalles
      .filter((d) => !d.es_medidor)
      .map((d) => ({ detalle_id: d.id, cantidad: Number(recepcion[d.id] ?? "") }))
      .filter((l) => l.cantidad > 0);
    const lineasMedidor: LineaRecepcionMedidor[] = oc.detalles
      .filter((d) => d.es_medidor)
      .map((d) => {
        const entry = recepcionMed[d.id];
        const l: LineaRecepcionMedidor = {
          detalle_id: d.id,
          series: parseSeries(entry?.series ?? ""),
        };
        if (entry?.marca?.trim()) l.marca = entry.marca.trim();
        if (entry?.diametro?.trim()) l.diametro = entry.diametro.trim();
        return l;
      })
      .filter((l) => l.series.length > 0);

    if (lineas.length === 0 && lineasMedidor.length === 0) {
      toast.error("Ingresa al menos una cantidad o N° de serie a recibir");
      return;
    }
    const bodegaElegida = bodegaId || (oc.bodega_destino_id != null ? String(oc.bodega_destino_id) : "");
    if (lineas.length > 0 && !bodegaElegida) {
      toast.error("Elige la bodega de destino para las líneas de stock");
      return;
    }
    const body: RecibirOCBody = {};
    if (lineas.length > 0) {
      body.bodega_id = Number(bodegaElegida);
      body.lineas = lineas;
    }
    if (lineasMedidor.length > 0) body.lineas_medidor = lineasMedidor;
    if (docTipo.trim()) body.documento_tipo = docTipo.trim();
    if (docNumero.trim()) body.numero_documento = docNumero.trim();
    recibirMut.mutate(body);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={oc ? `OC ${nroOC(oc)}` : "Orden de compra"}
      subtitle={oc ? oc.estado : undefined}
      icon={ShoppingCart}
      size="lg"
    >
      {!oc ? (
        <Cargando inline />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm sm:grid-cols-4">
            <div>
              <div className="text-xs text-neutral-500">Estado</div>
              <Badge tone={ESTADO_TONE[oc.estado]}>{oc.estado}</Badge>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Proveedor</div>
              <div className="font-medium text-neutral-800">#{oc.proveedor_id}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Condiciones</div>
              <div className="font-medium text-neutral-800">{oc.condiciones_pago ?? "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-neutral-500">Monto</div>
              <div className="font-semibold tabular-nums">{formatCLP(oc.monto_total)}</div>
            </div>
          </div>

          <TableWrap label="Detalle de la orden">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">Descripción</th>
                  <th scope="col" className="px-3 py-2 text-right">Cantidad</th>
                  <th scope="col" className="px-3 py-2 text-right">Recibido</th>
                  <th scope="col" className="px-3 py-2 text-right">Costo unit.</th>
                  <th scope="col" className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {oc.detalles.map((d) => (
                  <tr key={d.id}>
                    <td className="px-3 py-2 text-neutral-700">
                      {d.descripcion}
                      {d.es_medidor && (
                        <Badge tone="brand" className="ml-2">
                          Medidor
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{Number(d.cantidad)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-neutral-500">
                      {Number(d.cantidad_recibida)}/{Number(d.cantidad)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatCLP(d.costo_unitario)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCLP(d.costo_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>

          {oc.recepciones.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Recepciones de stock
              </div>
              <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
                {oc.recepciones.map((r) => (
                  <li key={r.id} className="space-y-1 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-neutral-700">
                        {new Date(r.fecha).toLocaleDateString("es-CL")}
                        {r.numero_documento ? ` · ${r.documento_tipo ?? "Doc."} ${r.numero_documento}` : ""}
                      </span>
                      <span className="text-neutral-500">
                        {r.detalles.map((d) => `${Number(d.cantidad)} × insumo #${d.insumo_id}`).join(", ")}
                      </span>
                      {r.estado === "Anulado" ? (
                        <Badge tone="slate" className="ml-auto">
                          Anulada
                        </Badge>
                      ) : (
                        oc.estado !== "Anulada" &&
                        anulandoRecepcion !== r.id && (
                          <Button
                            size="sm"
                            variant="linkDanger"
                            className="ml-auto"
                            onClick={() => setAnulandoRecepcion(r.id)}
                            disabled={anularRecepcionMut.isPending}
                          >
                            Anular recepción
                          </Button>
                        )
                      )}
                    </div>
                    {anulandoRecepcion === r.id && (
                      <div className="flex items-end gap-2">
                        <Input
                          label="Motivo (queda en auditoría)"
                          value={motivoAnulacion}
                          onChange={(e) => setMotivoAnulacion(e.target.value)}
                          containerClassName="flex-1"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (!motivoAnulacion.trim()) {
                              toast.error("Indica el motivo");
                              return;
                            }
                            anularRecepcionMut.mutate({
                              movimientoId: r.id,
                              motivo: motivoAnulacion.trim(),
                            });
                          }}
                          disabled={anularRecepcionMut.isPending}
                        >
                          Confirmar
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setAnulandoRecepcion(null)}
                        >
                          Cancelar
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {puedeRecibir &&
            (() => {
              const pendientes = oc.detalles.filter(
                (d) => Number(d.cantidad) - Number(d.cantidad_recibida) > 0,
              );
              const stockPend = pendientes.filter((d) => !d.es_medidor);
              const medPend = pendientes.filter((d) => d.es_medidor);
              return (
                <div className="space-y-3 rounded-md border border-brand-200 bg-brand-50 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-brand-900">
                    <PackageCheck className="h-4 w-4" strokeWidth={1.5} />
                    Recibir mercadería
                  </div>

                  {stockPend.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-brand-800">
                        El stock se carga en la bodega elegida y recalcula el precio promedio.
                      </p>
                      <div className="max-w-xs">
                        <Select
                          label="Bodega de destino"
                          value={bodegaId || (oc.bodega_destino_id != null ? String(oc.bodega_destino_id) : "")}
                          onChange={(e) => setBodegaId(e.target.value)}
                        >
                          <option value="">— Elegir —</option>
                          {(bodegasQ.data ?? []).map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.nombre}
                            </option>
                          ))}
                        </Select>
                      </div>
                      {stockPend.map((d) => {
                        const pendiente = Number(d.cantidad) - Number(d.cantidad_recibida);
                        return (
                          <div key={d.id} className="flex items-end gap-3">
                            <div className="flex-1">
                              <div className="text-sm text-neutral-700">{d.descripcion}</div>
                              <div className="text-xs text-neutral-500">Pendiente: {pendiente}</div>
                            </div>
                            <div className="w-28">
                              <Input
                                label="Recibir"
                                type="number"
                                min={0}
                                value={recepcion[d.id] ?? ""}
                                onChange={(e) =>
                                  setRecepcion((prev) => ({ ...prev, [d.id]: e.target.value }))
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {medPend.length > 0 && (
                    <div className="space-y-3 border-t border-brand-200 pt-3">
                      <p className="text-xs text-brand-800">
                        Los medidores se dan de alta en el padrón por su N° de serie (uno por
                        unidad). No van a stock de bodega.
                      </p>
                      {medPend.map((d) => {
                        const pendiente = Number(d.cantidad) - Number(d.cantidad_recibida);
                        const entry =
                          recepcionMed[d.id] ?? { series: "", marca: "", diametro: "" };
                        const cargados = parseSeries(entry.series).length;
                        return (
                          <div
                            key={d.id}
                            className="space-y-2 rounded-md border border-brand-200 bg-white p-2"
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium text-neutral-700">
                                {d.descripcion}
                              </div>
                              <div
                                className={cargados > pendiente ? "text-xs text-rose-700" : "text-xs text-neutral-500"}
                              >
                                Pendiente: {pendiente} · {cargados} cargado(s)
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                label="Marca (opcional)"
                                type="text"
                                value={entry.marca}
                                onChange={(e) =>
                                  setRecepcionMed((prev) => ({
                                    ...prev,
                                    [d.id]: { ...entry, marca: e.target.value },
                                  }))
                                }
                              />
                              <Select
                                label="Diámetro"
                                value={entry.diametro}
                                onChange={(e) =>
                                  setRecepcionMed((prev) => ({
                                    ...prev,
                                    [d.id]: { ...entry, diametro: e.target.value },
                                  }))
                                }
                              >
                                <option value="">— sin especificar —</option>
                                {/* Diámetros normados; mismos que el alta de medidor */}
                                {["13mm", "15mm", "20mm", "25mm", "40mm"].map((dm) => (
                                  <option key={dm} value={dm}>
                                    {dm}
                                  </option>
                                ))}
                              </Select>
                            </div>
                            <Textarea
                              label="N° de serie (uno por línea)"
                              rows={4}
                              placeholder={"A-1001\nA-1002\nA-1003"}
                              value={entry.series}
                              onChange={(e) =>
                                setRecepcionMed((prev) => ({
                                  ...prev,
                                  [d.id]: { ...entry, series: e.target.value },
                                }))
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 sm:max-w-md">
                    <Select
                      label="Documento del proveedor"
                      value={docTipo}
                      onChange={(e) => setDocTipo(e.target.value)}
                    >
                      <option value="">— Sin documento —</option>
                      <option value="Factura">Factura</option>
                      <option value="Guia">Guía de despacho</option>
                      <option value="Boleta">Boleta</option>
                    </Select>
                    <Input
                      label="N° de documento"
                      value={docNumero}
                      onChange={(e) => setDocNumero(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      icon={PackageCheck}
                      onClick={registrarRecepcion}
                      disabled={recibirMut.isPending}
                    >
                      {recibirMut.isPending ? "Registrando…" : "Registrar recepción"}
                    </Button>
                  </div>
                </div>
              );
            })()}

          {oc.estado === "Emitida" && (
            <div className="flex justify-end border-t border-neutral-100 pt-3">
              <Button
                variant="linkDanger"
                size="sm"
                onClick={() => anularMut.mutate()}
                disabled={anularMut.isPending}
              >
                {anularMut.isPending ? "Anulando…" : "Anular OC"}
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
